
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Badge,
} from '@mui/material';
import {
    Home as HomeIcon,
    Info as InfoIcon,
    Person as PersonIcon,
    WhatsApp as WhatsAppIcon,
    Event as EventIcon,
    People as PeopleIcon,
    Inventory as InventoryIcon,
    Payment as PaymentIcon,
    AccountBalance as AccountBalanceIcon,
    PhotoCamera as PhotoCameraIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    Login as LoginIcon,
    Place as PlaceIcon,
    Assessment as AnalyticsIcon,
} from '@mui/icons-material';
import { WhatsAppManageIcon } from '../icons/WhatsAppManageIcon';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, onSnapshot, query, where, doc, getDoc } from 'firebase/firestore';

interface MobileMenuProps {
    open: boolean;
    onClose: () => void;
    onOpenProfile: () => void;
    onOpenLogin: () => void;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
    open,
    onClose,
    onOpenProfile,
    onOpenLogin,
}) => {
    const { user, isAdmin, logout } = useAuth();
    const navigate = useNavigate();
    const [unverifiedUsersCount, setUnverifiedUsersCount] = useState(0);
    const [nickname, setNickname] = useState('');

    useEffect(() => {
        const fetchNickname = async () => {
            if (user) {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    setNickname(userDoc.data().nickname);
                }
            }
        };
        fetchNickname();
    }, [user]);

    useEffect(() => {
        if (isAdmin) {
            const unsubscribe = onSnapshot(
                query(collection(db, 'users'), where('verified', '==', false)),
                (snapshot) => {
                    setUnverifiedUsersCount(snapshot.docs.length);
                }
            );
            return () => unsubscribe();
        }
    }, [isAdmin]);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
            onClose();
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: 250,
                    bgcolor: '#2c3e50',
                    color: '#fff'
                }
            }}
        >
            <List>
                <ListItem
                    button
                    component={RouterLink}
                    to="/"
                    onClick={onClose}
                >
                    <ListItemIcon>
                        <HomeIcon sx={{ color: '#fff' }} />
                    </ListItemIcon>
                    <ListItemText primary="Accueil" />
                </ListItem>

                <ListItem
                    button
                    component={RouterLink}
                    to="/qui-sommes-nous"
                    onClick={onClose}
                >
                    <ListItemIcon>
                        <InfoIcon sx={{ color: '#fff' }} />
                    </ListItemIcon>
                    <ListItemText primary="Qui sommes-nous ?" />
                </ListItem>

                <ListItem
                    button
                    component={RouterLink}
                    to="/locations"
                    onClick={onClose}
                >
                    <ListItemIcon>
                        <PlaceIcon sx={{ color: '#fff' }} />
                    </ListItemIcon>
                    <ListItemText primary="Nos Salles" />
                </ListItem>

                {user ? (
                    <>
                        <ListItem
                            button
                            onClick={() => {
                                onClose();
                                onOpenProfile();
                            }}
                        >
                            <ListItemIcon>
                                <PersonIcon sx={{ color: '#fff' }} />
                            </ListItemIcon>
                            <ListItemText primary={nickname || 'Profil'} />
                        </ListItem>

                        <ListItem
                            button
                            component={RouterLink}
                            to="/communication"
                            onClick={onClose}
                        >
                            <ListItemIcon>
                                <WhatsAppIcon sx={{ color: '#fff' }} />
                            </ListItemIcon>
                            <ListItemText primary="Communication" />
                        </ListItem>

                        <ListItem
                            button
                            component={RouterLink}
                            to="/events"
                            onClick={onClose}
                        >
                            <ListItemIcon>
                                <EventIcon sx={{ color: '#fff' }} />
                            </ListItemIcon>
                            <ListItemText primary="Événements" />
                        </ListItem>

                        {isAdmin && (
                            <>
                                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
                                <ListItem
                                    button
                                    component={RouterLink}
                                    to="/admin/users"
                                    onClick={onClose}
                                >
                                    <ListItemIcon>
                                        <Badge badgeContent={unverifiedUsersCount} color="error">
                                            <PeopleIcon sx={{ color: '#fff' }} />
                                        </Badge>
                                    </ListItemIcon>
                                    <ListItemText primary="Gestion des adhérents" />
                                </ListItem>

                                <ListItem
                                    button
                                    component={RouterLink}
                                    to="/admin/inventory"
                                    onClick={onClose}
                                >
                                    <ListItemIcon>
                                        <InventoryIcon sx={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Inventaire" />
                                </ListItem>

                                <ListItem
                                    button
                                    component={RouterLink}
                                    to="/admin/subscriptions"
                                    onClick={onClose}
                                >
                                    <ListItemIcon>
                                        <PaymentIcon sx={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Cotisations" />
                                </ListItem>

                                <ListItem
                                    button
                                    component={RouterLink}
                                    to="/admin/finances"
                                    onClick={onClose}
                                >
                                    <ListItemIcon>
                                        <AccountBalanceIcon sx={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Finances" />
                                </ListItem>

                                <ListItem
                                    button
                                    component={RouterLink}
                                    to="/admin/social"
                                    onClick={onClose}
                                >
                                    <ListItemIcon>
                                        <WhatsAppManageIcon sx={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Gestion Communication" />
                                </ListItem>

                                <ListItem
                                    button
                                    component={RouterLink}
                                    to="/admin/photo-import"
                                    onClick={onClose}
                                >
                                    <ListItemIcon>
                                        <PhotoCameraIcon sx={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Import Photos" />
                                </ListItem>

                                <ListItem
                                    button
                                    component={RouterLink}
                                    to="/admin/theme"
                                    onClick={onClose}
                                >
                                    <ListItemIcon>
                                        <SettingsIcon sx={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Personnalisation" />
                                </ListItem>

                                <ListItem
                                    button
                                    component={RouterLink}
                                    to="/admin/analytics"
                                    onClick={onClose}
                                >
                                    <ListItemIcon>
                                        <AnalyticsIcon sx={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Analytique" />
                                </ListItem>
                            </>
                        )}

                        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
                        <ListItem
                            button
                            onClick={handleLogout}
                            sx={{ color: '#ff1744' }}
                        >
                            <ListItemIcon>
                                <LogoutIcon sx={{ color: '#ff1744' }} />
                            </ListItemIcon>
                            <ListItemText primary="Déconnexion" />
                        </ListItem>
                    </>
                ) : (
                    <ListItem
                        button
                        onClick={() => {
                            onClose();
                            onOpenLogin();
                        }}
                    >
                        <ListItemIcon>
                            <LoginIcon sx={{ color: '#fff' }} />
                        </ListItemIcon>
                        <ListItemText primary="Connexion" />
                    </ListItem>
                )}
            </List>
        </Drawer>
    );
};
